import { HIDE_ON_HOVER, SHOW_ON_HOVER } from "lib/core_components/ShowOnHover";
import { IconProps } from "lib/icons/untitled_ui/UIcon";
import { pluralise } from "../../../lib/utils";
import EntityTag from "../../entities/EntityTag";
import { SharedSaveProps } from "../../entities/utils";
import { ButtonType } from "../../ui/button/Button";
import { CreateLeadFormOutput } from "../create_lead/utils";
import { SaveLeadButton } from "./SaveLeadButton";
import { useSaveLeadDrawer } from "./SaveLeadDrawerProvider";

type DisplayType = "showSavedLeads" | "button";

type InRowSaveToButtonProps = SharedSaveProps<CreateLeadFormOutput> & {
  displayType: DisplayType;
  savedLeads: {
    id: string;
    name: string;
  }[];
  /** Optional override for the underlying SaveLeadButton (e.g. an icon-only bookmark). */
  buttonProps?: {
    buttonText?: string;
    buttonType?: ButtonType;
    icon?: IconProps | null;
  };
};

/**
 * Changes display depending on how many leads the entity has been saved to.
 * This button needs to be used within the context of @see SaveLeadDrawerProvider.
 * @param param0
 * @returns
 */
export function InlineSaveLeadButton({
  displayType,
  savedLeads,
  ...props
}: InRowSaveToButtonProps) {
  const { openLeadDrawer } = useSaveLeadDrawer();
  const savedLeadIds = savedLeads.map((lead) => lead.id);

  const savedCount = savedLeadIds.length;

  const saveButton = (
    <SaveLeadButton
      {...props}
      savedLeadIds={savedLeads.map((lead) => lead.id)}
      trackingData={{ "Context source": "In row" }}
      onShowDrawer={() =>
        openLeadDrawer({
          ...props,
        })
      }
    />
  );

  // If it's not saved to any leads, show the button only on hover
  if (savedCount === 0) {
    return <span className={SHOW_ON_HOVER}>{saveButton}</span>;
  }

  // If we get here, the entity has been saved to a lead
  if (displayType === "showSavedLeads") {
    return (
      <span className={HIDE_ON_HOVER}>
        <EntityTag
          title={`Saved to ${savedCount > 1 ? pluralise(savedCount, "lead") : `${savedLeads[0].name}`}`}
          icon="target04"
        />
      </span>
    );
  }

  return saveButton;
}
